import React, { useState } from "react";
import {
  Box,
  Divider,
  Typography,
  Paper,
  Chip,
  Grid,
  TextField,
  IconButton,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import LockIcon from "@mui/icons-material/Lock";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { toast } from "react-toastify";
import { api } from "../services/api";
import { useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

const AdditionalTag = ({ resourceOverview, resourceTypeId }) => {
  const [tags, setTags] = useState([{ name: "", value: "" }]);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();


  // Get region as in DynamicDiskForm
  const locationObj = resourceOverview?.resourceRequestDetails?.find?.(
    (item) => item.fieldName === "location"
  );
  const region = locationObj ? locationObj.fieldValue : "";
  const cloudProvider = localStorage.getItem("selectedProviderId");
  const existingTags = resourceOverview?.resourceRequestDetails?.filter(
    (item) => item.tabName === "Tag"
  ) || [];

  const TAG_CATEGORIES = {
    GENERAL: "General",
    OPTIONAL: "Optional",
    LOGIC_MONITOR: "Logic Monitor",
  };

  const categorizeTag = (subTab) => {
    if (!subTab) return TAG_CATEGORIES.OPTIONAL;

    const lower = subTab.toLowerCase();
    if (lower.includes("logic") || lower.includes("monitor")) {
      return TAG_CATEGORIES.LOGIC_MONITOR;
    }
    if (lower.includes("optional")) {
      return TAG_CATEGORIES.OPTIONAL;
    }
    return TAG_CATEGORIES.GENERAL;
  };

  const groupedExistingTags = existingTags.reduce(
    (acc, tag) => {
      const category = categorizeTag(tag.subTab);
      acc[category].push(tag);
      return acc;
    },
    {
      [TAG_CATEGORIES.GENERAL]: [],
      [TAG_CATEGORIES.OPTIONAL]: [],
      [TAG_CATEGORIES.LOGIC_MONITOR]: [],
    }
  );
  // Get resourceType (with fallback)
  const resourceType =
    resourceOverview?.resourceType ||
    location?.state?.resourceType ||
    location?.state?.resourceId ||
    "";

  // Validation helper
  const validateTags = () => {
    let valid = true;
    const names = new Set();
    for (let i = 0; i < tags.length; i++) {
      const { name, value } = tags[i];
      if (!name.trim() || !value.trim()) {
        toast.error("Tag name and value cannot be empty.");
        valid = false;
        break;
      }
      if (names.has(name.trim())) {
        toast.error("Duplicate tag names are not allowed.");
        valid = false;
        break;
      }
      names.add(name.trim());
    }
    return valid;
  };

  const handleAddTag = () => {
    setTags((prev) => [...prev, { name: "", value: "" }]);
  };

  const handleRemoveTag = (idx) => {
    setTags((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleTagChange = (idx, field, value) => {
    setTags((prev) =>
      prev.map((tag, i) =>
        i === idx ? { ...tag, [field]: value } : tag
      )
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Remove blank rows
    const filtered = tags.filter(tag => tag.name.trim() && tag.value.trim());

    // If removing all tags (no tags left), allow that (send empty object)
    if (filtered.length === 0) {
      await submitTags({});
      setTags([{ name: "", value: "" }]);
      return;
    }

    setTags(filtered);

    if (!validateTags()) return;

    // Build tags object for payload (no wrapper)
    const tagObj = {};
    filtered.forEach(tag => {
      tagObj[tag.name.trim()] = tag.value.trim();
    });

    await submitTags(tagObj);
  };


  const handleReset = () => {
    setTags([{ name: "", value: "" }]);
  };
  // This function handles the API call
  const submitTags = async (tagObj) => {
    setLoading(true);
    const payload = {
      applicationName: resourceOverview?.applicationName || "",
      subscription: localStorage.getItem("selectedSubscription") || "",
      totalPrice: "0",
      resourceRequest: JSON.stringify(tagObj), // <<--- NO wrapper
      requester: sessionStorage.getItem("userId") || localStorage.getItem("userIds"),
      resourceType: resourceTypeId,
      organizationId: Number.parseInt(sessionStorage.getItem("organizationId")),
      Parent_Resource_ID: resourceOverview?.resource_Id || 0,
      region,
      cloudProvider,
      operationId: 60,
      operationName: "Additional Tags"
    };
    try {
      await api.post("/api/Resources/createOperationalRequest", payload, {
        headers: { "Content-Type": "application/json" },
      });
      toast.success("Tags submitted successfully.");
      handleReset();
      navigate("/provision-status");
    } catch (error) {
      toast.error("Failed to submit tags.");
      handleReset();
      navigate('/Resources/View_Resources');
    }
    setLoading(false);
  };

  const handleDiscard = () => {
    setTags([{ name: "", value: "" }]);
    toast.info("Changes discarded.");
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto" }}>
      {existingTags.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Existing Tags
            </Typography>
            <Chip label={`${existingTags.length} tags`} size="small" />
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, display: "flex", alignItems: "center" }}>
            <LockIcon sx={{ fontSize: 16, color: "text.secondary", mr: 1 }} />
            Tags marked with a lock icon are protected and cannot be edited.
          </Typography>

          {Object.entries(groupedExistingTags).map(([category, categoryTags]) =>
            categoryTags.length > 0 ? (
              <Accordion key={category} sx={{ mb: 1.5 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 14 }}>
                    {category}
                  </Typography>
                  {/* <Chip label={`${categoryTags.length} tags`} size="small" sx={{ ml: 2 }} /> */}
                </AccordionSummary>

                <AccordionDetails>
                  {categoryTags.map((tag, idx) => (
                    <Box key={`${tag.fieldName || "tag"}-${idx}`} sx={{ mb: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500, mr: 1 }}>
                              Tag Name
                            </Typography>
                            <LockIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                          </Box>
                          <TextField
                            value={tag.fieldName || ""}
                            size="small"
                            fullWidth
                            slotProps={{ input: { readOnly: true } }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500, mr: 1 }}>
                              Tag Value
                            </Typography>
                            <LockIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                          </Box>
                          <TextField
                            value={tag.fieldData || tag.fieldValue || ""}
                            size="small"
                            fullWidth
                            slotProps={{ input: { readOnly: true } }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>
            ) : null
          )}

          <Divider sx={{ mt: 3 }} />
        </Box>
      )}

      <form onSubmit={handleSubmit}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
          Add New Tags
        </Typography>
        <Typography variant="body2" color="#888">
          Tag names must be unique and not empty. Avoid sensitive information.
        </Typography>
        <Box sx={{ mt: 2, mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2.5, mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500, width: 240 }}>
              Tag Name
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 500, width: 240 }}>
              Tag Value
            </Typography>
            <Box sx={{ width: 40 }} />
          </Box>

          {tags.map((tag, idx) => (
            <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 2.5, mb: 1.5 }}>
              <TextField
                value={tag.name}
                onChange={e => handleTagChange(idx, "name", e.target.value)}
                placeholder="Enter tag name"
                size="small"
                variant="outlined"
                required
                sx={{ width: 240 }}
              />
              <TextField
                value={tag.value}
                onChange={e => handleTagChange(idx, "value", e.target.value)}
                placeholder="Enter tag value"
                size="small"
                variant="outlined"
                required
                sx={{ width: 240 }}
              />
              <IconButton
                onClick={() => handleRemoveTag(idx)}
                disabled={tags.length === 1}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 5 }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => handleAddTag()}
            disabled={loading}
          >
            Add Tag
          </Button>

          <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="cancelContained"
            color="secondary"
            onClick={() => handleDiscard()}
            disabled={loading}
          >
            Discard 
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? "Saving..." : "Save Changes"}
          </Button>
          </Box>
        </Box>
      </form>
    </Box>
  );
};

AdditionalTag.propTypes = {
  resourceOverview: PropTypes.shape({
    applicationName: PropTypes.string,
    resourceType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    resource_Id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    resourceRequestDetails: PropTypes.arrayOf(
      PropTypes.shape({
        fieldName: PropTypes.string,
        fieldValue: PropTypes.any,
      })
    ),
  }),

  resourceTypeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default AdditionalTag;